import cx from 'classnames'
import { Text } from 'blaze-ui/Text/Text'
import Styles from './DowngradeConfirmation.module.scss'

import { Modal, useModals } from 'almanac-ui/Modal'
import { Heading } from 'blaze-ui/Heading/Heading'
import { ArrowDownSquareContained, ArrowRightSquareContained, CheckBroken } from 'blaze-ui/icons'
import XSquareContained from 'blaze-ui/icons/24/XSquareContained'
import { FREE_REWARD_TIERS } from 'hooks/useAutopilotLimits'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import dayjs from 'dayjs'
import { useRootStore } from 'stores/RootStore'
import { PaymentPlanKey } from 'api/clients/PaymentPlanClient'
import { findPlanCreditLimit } from 'components/Settings/Workspace/AutopilotPlans/utils'

const SecondaryDescription = ({
  isTrialing,
  fromGrowthToStarter,
  fromGrowthToFree,
  trialDaysLeft,
}: {
  isTrialing: boolean
  fromGrowthToStarter: boolean
  fromGrowthToFree: boolean
  trialDaysLeft: number
}) => {
  if (isTrialing && fromGrowthToStarter) {
    return (
      <Text variant="secondary">
        Your Growth trial will switch to a Starter trial right away and will end on{' '}
        {dayjs().add(trialDaysLeft, 'day').format('MMM D, YYYY')}.
      </Text>
    )
  }
  if (!isTrialing && fromGrowthToStarter) {
    return (
      <Text variant="secondary">
        Your Starter plan starts right away. The prorated balance from your Growth plan will be applied to your next
        invoice.
      </Text>
    )
  }
  if (!isTrialing && fromGrowthToFree) {
    return <Text variant="secondary">Upgrade anytime to regain full access.</Text>
  }
  return null
}

const FromGrowth = ({ isTrialing }: { isTrialing: boolean }) => {
  const { tierUsageStore } = useRootStore()
  const growthCreditLimit = Number(tierUsageStore.currentSubscription?.prices?.[0]?.metadata?.credit_limit ?? 0)
  const trialCredits = Math.round(growthCreditLimit / 4)

  return (
    <>
      <div className={Styles.header}>
        <Text variant="label">Your current plan</Text>
        <Heading level={4}>Growth</Heading>
      </div>
      <div className={Styles.detail}>
        <CheckBroken color="var(--green-50)" size={22} />
        <Text fontWeight="700">10 accounts</Text>
      </div>
      <div className={Styles.detail}>
        <CheckBroken color="var(--green-50)" size={22} />
        <Text fontWeight="700">Unlimited seats</Text>
      </div>
      <div className={Styles.detail}>
        <CheckBroken color="var(--green-50)" size={22} />
        <Text fontWeight="700">
          {isTrialing ? trialCredits.toLocaleString() : growthCreditLimit.toLocaleString()} content credits{' '}
          {isTrialing ? 'during trial' : 'per month'}
        </Text>
      </div>
    </>
  )
}

const FromPioneer = () => {
  return (
    <>
      <div className={Styles.header}>
        <Text variant="label">Your current plan</Text>
        <Heading level={4}>Pioneer</Heading>
      </div>
      <div className={Styles.detail}>
        <CheckBroken color="var(--green-50)" size={22} />
        <Text fontWeight="700">Parallel campaigns</Text>
      </div>
      <div className={Styles.detail}>
        <CheckBroken color="var(--green-50)" size={22} />
        <Text fontWeight="700" lineClamp={1}>
          Crossposting
        </Text>
      </div>
      <div className={Styles.detail}>
        <CheckBroken color="var(--green-50)" size={22} />
        <Text fontWeight="700">Multi-user workflow (reviews & approvals)</Text>
      </div>
      <div className={Styles.detail}>
        <CheckBroken color="var(--green-50)" size={22} />
        <Text fontWeight="700">AI-powered ad management</Text>
      </div>
      <div className={Styles.detail}>
        <CheckBroken color="var(--green-50)" size={22} />
        <Text fontWeight="700">Unlimited accounts</Text>
      </div>
      <div className={Styles.detail}>
        <CheckBroken color="var(--green-50)" size={22} />
        <Text fontWeight="700">Unlimited seats</Text>
      </div>
    </>
  )
}

const FromStarter = () => {
  return (
    <>
      <div className={Styles.header}>
        <Text variant="label">Your current plan</Text>
        <Heading level={4}>Starter</Heading>
      </div>
      <div className={Styles.detail}>
        <CheckBroken color="var(--green-50)" size={22} />
        <Text fontWeight="700">No Blaze watermark</Text>
      </div>
      <div className={Styles.detail}>
        <CheckBroken color="var(--green-50)" size={22} />
        <Text fontWeight="700" lineClamp={1}>
          3 accounts
        </Text>
      </div>
      <div className={Styles.detail}>
        <CheckBroken color="var(--green-50)" size={22} />
        <Text fontWeight="700">Unlimited posts per week per channel</Text>
      </div>
    </>
  )
}

const ToStarter = ({ isTrialing }: { isTrialing: boolean }) => {
  const { tierUsageStore } = useRootStore()
  const starterCreditLimit = findPlanCreditLimit(tierUsageStore.autopilotPlans, PaymentPlanKey.BlazeAutopilotStarter)
  const trialCredits = Math.round(starterCreditLimit / 4)

  return (
    <>
      <div className={Styles.detail}>
        <ArrowDownSquareContained color="var(--orange-50)" size={22} />
        <div className={Styles.detailText}>
          <Text>
            <strong>3</strong> accounts
          </Text>
        </div>
      </div>
      <div className={Styles.detail}>
        <ArrowDownSquareContained color="var(--orange-50)" size={22} />
        <div className={Styles.detailText}>
          <Text>
            <strong>1</strong> seat
          </Text>
        </div>
      </div>
      <div className={Styles.detail}>
        <ArrowDownSquareContained color="var(--orange-50)" size={22} />
        <div className={Styles.detailText}>
          <Text>
            <strong>{isTrialing ? trialCredits.toLocaleString() : starterCreditLimit.toLocaleString()}</strong> content
            credits {isTrialing ? 'during trial' : 'per month'}
          </Text>
        </div>
      </div>
    </>
  )
}

const ToFree = ({ fromPlan }: { fromPlan: string }) => {
  if (fromPlan === 'Starter') {
    return (
      <>
        <div className={Styles.detail}>
          <ArrowDownSquareContained color="var(--orange-50)" size={22} />
          <div className={Styles.detailText}>
            <Text>
              <strong>Blaze watermark</strong> on all content
            </Text>
          </div>
        </div>
        <div className={Styles.detail}>
          <ArrowRightSquareContained color="var(--dark-40)" size={22} />
          <div className={Styles.detailText}>
            <Text>
              <strong>3</strong> Channels
            </Text>
          </div>
        </div>
        <div className={Styles.detail}>
          <ArrowDownSquareContained color="var(--orange-50)" size={22} />
          <div className={Styles.detailText}>
            <Text>
              <strong>1</strong> Post per week per channel
            </Text>
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      <div className={Styles.detail}>
        <XSquareContained color="var(--red-70)" size={22} />
        <div className={Styles.multiLineText}>
          <Text>
            <strong>No</strong> strategic multi-week campaigns – you’ll get simple weekly batches instead.
          </Text>
        </div>
      </div>
      <div className={Styles.detail}>
        <XSquareContained color="var(--red-70)" size={22} />
        <div className={Styles.detailText}>
          <Text>
            <strong>No</strong> crossposting
          </Text>
        </div>
      </div>
      <div className={Styles.detail}>
        <XSquareContained color="var(--red-70)" size={22} />
        <div className={Styles.detailText}>
          <Text>
            <strong>No</strong> multi-user workflow (reviews & approvals)
          </Text>
        </div>
      </div>
      <div className={Styles.detail}>
        <XSquareContained color="var(--red-70)" size={22} />
        <div className={Styles.detailText}>
          <Text>
            <strong>No</strong> AI-powered ad management
          </Text>
        </div>
      </div>
      <div className={Styles.detail}>
        <ArrowDownSquareContained color="var(--orange-50)" size={22} />
        <div className={Styles.detailText}>
          <Text>
            <strong>Blaze watermark</strong> on all content
          </Text>
        </div>
      </div>
      <div className={Styles.detail}>
        <ArrowDownSquareContained color="var(--orange-50)" size={22} />
        <div className={Styles.detailText}>
          <Text>
            <strong>3</strong> channels
          </Text>
        </div>
      </div>
      <div className={Styles.detail}>
        <ArrowDownSquareContained color="var(--orange-50)" size={22} />
        <div className={Styles.detailText}>
          <Text>
            <strong>1</strong> seat
          </Text>
        </div>
      </div>
      <div className={Styles.detail}>
        <ArrowDownSquareContained color="var(--orange-50)" size={22} />
        <div className={Styles.detailText}>
          <Text>
            <strong>{FREE_REWARD_TIERS[0]}</strong> post per week per channel
          </Text>
        </div>
      </div>
    </>
  )
}

export const DowngradeConfirmation = ({
  isStarterPlan,
  currentPlanName,
  onBack,
  onConfirm,
  trialDaysLeft,
  isDisabled,
}: {
  isStarterPlan: boolean
  currentPlanName: string
  onBack: () => void
  onConfirm: () => void
  trialDaysLeft: number
  isDisabled?: boolean
}) => {
  const { closeModal } = useModals()
  const { isMobile } = useResponsiveDesign()
  const toPlanName = isStarterPlan ? 'Starter' : 'Free'
  const fromGrowthToStarter = currentPlanName === 'Growth' && toPlanName === 'Starter'
  const fromGrowthToFree = currentPlanName === 'Growth' && toPlanName === 'Free'
  const isTrialing = trialDaysLeft > 0
  const hasSecondaryText =
    (isTrialing && fromGrowthToStarter) || (!isTrialing && (fromGrowthToStarter || fromGrowthToFree))

  const headerTitle = isMobile
    ? `Downgrading to ${toPlanName} ${isTrialing ? 'trial' : 'plan'}`
    : `You're downgrading to the ${toPlanName} ${isTrialing ? 'trial' : 'plan'}`

  return (
    <>
      <Modal.Header>
        <div className={Styles.header}>
          <Heading level={3}>{headerTitle}</Heading>
          {!isMobile && hasSecondaryText && (
            <SecondaryDescription
              isTrialing={isTrialing}
              fromGrowthToStarter={fromGrowthToStarter}
              fromGrowthToFree={fromGrowthToFree}
              trialDaysLeft={trialDaysLeft}
            />
          )}
        </div>
      </Modal.Header>
      <Modal.Content>
        {isMobile && hasSecondaryText && (
          <div className={Styles.descriptionBlock}>
            <SecondaryDescription
              isTrialing={isTrialing}
              fromGrowthToStarter={fromGrowthToStarter}
              fromGrowthToFree={fromGrowthToFree}
              trialDaysLeft={trialDaysLeft}
            />
          </div>
        )}
        <div className={Styles.content}>
          <div className={cx(Styles.details, Styles.left)}>
            {currentPlanName === 'Growth' && <FromGrowth isTrialing={isTrialing} />}
            {currentPlanName === 'Starter' && <FromStarter />}
            {currentPlanName === 'Pioneer' && <FromPioneer />}
          </div>
          <div className={cx(Styles.details, Styles.right)}>
            <div className={Styles.header}>
              <Text variant="label">Your new plan</Text>
              <Heading level={4}>{toPlanName}</Heading>
            </div>
            {isStarterPlan ? <ToStarter isTrialing={isTrialing} /> : <ToFree fromPlan={currentPlanName} />}
          </div>
        </div>
      </Modal.Content>
      <Modal.Footer>
        <Modal.FooterContent slot="left">
          <Modal.FooterButton variant="tertiary" onPress={onBack} isDisabled={isDisabled}>
            Back
          </Modal.FooterButton>
        </Modal.FooterContent>
        <Modal.FooterContent slot="right" className={Styles.footerRight}>
          <Modal.FooterButton variant="tertiary" onPress={closeModal} isDisabled={isDisabled}>
            Stay on {currentPlanName}
          </Modal.FooterButton>
          <Modal.FooterButton onPress={onConfirm} isDisabled={isDisabled}>
            Continue
          </Modal.FooterButton>
        </Modal.FooterContent>
      </Modal.Footer>
    </>
  )
}
